/**
 * Carry-forward logic — determines which symptoms from the previous session
 * should appear in the copy-forward panel of the next log.
 */
import { SYMPTOM_MAP } from './taxonomy';

const EXPIRY_MS = {
  expire48: 48 * 60 * 60 * 1000,
  expire36: 36 * 60 * 60 * 1000,
};

/**
 * Given a previous session object and current timestamp, return which symptoms
 * should appear in the copy-forward panel.
 *
 * Each returned item has:
 *   - All original symptom fields (key, attributes, etc.)
 *   - needsConfirmation: true if gap > 24h (soft "still experiencing?" prompt)
 *   - isPromptType: true if the symptom should show an update prompt (bleeding, discharge)
 *
 * @param {object|null} previousSession
 * @param {number} nowMs — Date.now()
 * @returns {object[]}
 */
export function getCopyForwardSymptoms(previousSession, nowMs = Date.now()) {
  if (!previousSession?.symptoms?.length) return [];

  const prevMs = new Date(previousSession.logged_at).getTime();
  const gapMs = nowMs - prevMs;
  const gapHours = gapMs / (1000 * 60 * 60);

  return previousSession.symptoms
    .filter(symptom => {
      const def = SYMPTOM_MAP[symptom.key];
      if (!def) return false;

      const cf = def.carryForward;
      if (cf === 'reset') return false;
      if (cf === 'carry' || cf === 'prompt') return true;
      if (cf === 'expire48') return gapMs < EXPIRY_MS.expire48;
      if (cf === 'expire36') return gapMs < EXPIRY_MS.expire36;
      return false;
    })
    .map(symptom => ({
      ...symptom,
      needsConfirmation: gapHours > 24,
      isPromptType: SYMPTOM_MAP[symptom.key]?.carryForward === 'prompt',
    }));
}

/**
 * Format a relative time string for the copy-forward header.
 * e.g. "6 hours ago", "Yesterday at 7pm", "2 days ago"
 *
 * @param {string} loggedAt — ISO timestamp
 * @returns {string}
 */
export function formatLastLogTime(loggedAt) {
  if (!loggedAt) return 'your last log';
  const then = new Date(loggedAt);
  const now = new Date();
  const diffMs = now - then;
  const diffH = diffMs / (1000 * 60 * 60);
  const diffD = diffMs / (1000 * 60 * 60 * 24);

  if (diffH < 1) return 'just now';
  if (diffH < 2) return '1 hour ago';
  if (diffH < 24) return `${Math.round(diffH)} hours ago`;
  if (diffH < 36) {
    const timeStr = then.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit' });
    return `yesterday at ${timeStr}`;
  }
  if (diffD < 7) return `${Math.round(diffD)} days ago`;
  return then.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/**
 * Compute symptom memory patterns from the last N sessions.
 * Returns a map of symptomKey → { count, commonAttributes }
 * where commonAttributes contains attribute values that appeared in ≥60% of sessions where
 * this symptom was logged.
 *
 * @param {object[]} sessions — array of session objects, most recent first
 * @param {number} limit — how many sessions to look back (default 10)
 * @returns {object}
 */
export function computePatternMemory(sessions = [], limit = 10) {
  const recent = sessions.slice(0, limit);
  const memory = {};

  // Collect all attribute values per symptom per session
  const counts = {}; // { symptomKey: { attributeKey: { value: count } } }
  const sessionCounts = {}; // { symptomKey: sessionCount }

  recent.forEach(session => {
    const seen = new Set();
    (session.symptoms || []).forEach(symptom => {
      const key = symptom.key;
      if (seen.has(key)) return;
      seen.add(key);

      sessionCounts[key] = (sessionCounts[key] || 0) + 1;
      if (!counts[key]) counts[key] = {};

      Object.entries(symptom.attributes || {}).forEach(([attrKey, value]) => {
        if (!value) return;
        if (!counts[key][attrKey]) counts[key][attrKey] = {};
        counts[key][attrKey][value] = (counts[key][attrKey][value] || 0) + 1;
      });
    });
  });

  // For each symptom, find attributes consistent in ≥60% of logged sessions
  Object.entries(counts).forEach(([symptomKey, attrMap]) => {
    const total = sessionCounts[symptomKey] || 1;
    const commonAttributes = {};

    Object.entries(attrMap).forEach(([attrKey, valueCounts]) => {
      const [topValue, topCount] = Object.entries(valueCounts)
        .sort((a, b) => b[1] - a[1])[0];
      if (topCount / total >= 0.6) {
        commonAttributes[attrKey] = topValue;
      }
    });

    if (Object.keys(commonAttributes).length > 0 && total >= 3) {
      memory[symptomKey] = { count: total, commonAttributes };
    }
  });

  return memory;
}
